import React from 'react';
import { Link } from 'react-router-dom';
import { Column, type ColumnGroupMeta } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { deleteSupplyForwarding } from '../../../services/mutations';
import type { SupplyForwarding } from './useData';
import { supplyBlockedActionMessage } from '../blockingMessages';

export const moduleConfig = {
  module: {
    title: 'Supply forwardings',
    permissionModuleKey: 'supply_forwardings',
    route: {
      list: '/supplies/supply-forwardings',
      new: '/supplies/supply-forwardings/new',
      detail: (id: number) => `/supplies/supply-forwardings/${id}`,
      edit: (id: number) => `/supplies/supply-forwardings/${id}/edit`},
    api: {
      list: '/supply-forwardings',
      detail: (id: number) => `/supply-forwardings/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference, notes, case, vessel, order, currency, port or status…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['ref', 'etaToPort', 'status', 'priority', 'warehouseLocation', 'forwarder']},
    {
      name: 'case',
      label: 'Case',
      icon: '',
      fields: ['vessel', 'caseRef', 'caseDate', 'caseEta', 'casePort', 'caseStatus']},
    {
      name: 'order',
      label: 'Order',
      icon: '',
      fields: ['orderRef', 'orderDate']},
  ],
  filters: {
    fields: [
      { key: 'vessel', label: 'Vessel', type: 'select' as const, placeholder: 'All vessels', getValue: (item: SupplyForwarding) => item.supplyCase?.vessel?.vesselName || '', options: [] },
      { key: 'caseRef', label: 'Case ref', type: 'select' as const, placeholder: 'All case refs', getValue: (item: SupplyForwarding) => item.supplyCase?.ref || '', options: [] },
      { key: 'caseDate', label: 'Case date', type: 'date' as const, placeholder: 'All case dates', getValue: (item: SupplyForwarding) => item.supplyCase?.caseDate || '', options: [] },
      { key: 'caseEta', label: 'Case ETA', type: 'date' as const, placeholder: 'All case ETAs', getValue: (item: SupplyForwarding) => item.supplyCase?.eta || '', options: [] },
      { key: 'casePort', label: 'Case port', type: 'select' as const, placeholder: 'All case ports', getValue: (item: SupplyForwarding) => formatCasePort(item.supplyCase?.port) || '', options: [] },
      { key: 'caseStatus', label: 'Case status', type: 'select' as const, placeholder: 'All case statuses', getValue: (item: SupplyForwarding) => item.supplyCase?.status?.name || '', options: [] },
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: SupplyForwarding) => item.ref || '',
        options: []},
      { key: 'etaToPort', label: 'ETA to port', type: 'date' as const, placeholder: 'All ETAs', getValue: (item: SupplyForwarding) => item.etaToPort || '', options: [] },
      { key: 'orderRef', label: 'Order ref', type: 'select' as const, placeholder: 'All order refs', getValue: (item: SupplyForwarding) => item.purchaseOrder?.ref || '', options: [] },
      { key: 'orderDate', label: 'Order date', type: 'date' as const, placeholder: 'All order dates', getValue: (item: SupplyForwarding) => item.purchaseOrder?.orderDate || '', options: [] },
      { key: 'status', label: 'Status', type: 'select' as const, placeholder: 'All statuses', getValue: (item: SupplyForwarding) => item.status?.name || '', options: [] },
      { key: 'priority', label: 'Priority', type: 'select' as const, placeholder: 'All priorities', getValue: (item: SupplyForwarding) => item.priority?.name || '', options: [] },
      { key: 'warehouseLocation', label: 'WH location', type: 'select' as const, placeholder: 'All locations', getValue: (item: SupplyForwarding) => item.warehouseLocation?.name || '', options: [] },
      { key: 'forwarder', label: 'Forwarder', type: 'select' as const, placeholder: 'All forwarders', getValue: (item: SupplyForwarding) => item.forwarder?.name || '', options: [] },
    ],
    applyFilter: (
      items: SupplyForwarding[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      const dateMatches = (
        rawDate: string | null | undefined,
        values: string[],
        range: { start: Date | null; end: Date | null } | undefined,
      ): boolean => {
        if (range?.start || range?.end) {
          const d = rawDate ? new Date(rawDate) : null;
          if (!d || Number.isNaN(d.getTime())) return false;
          if (range.start && d < range.start) return false;
          if (range.end && d > range.end) return false;
          return true;
        }
        if (values.length === 0) return true;
        const itemDate = toDateOnly(rawDate);
        return values.some((v) => itemDate.includes(String(v).slice(0, 10)));
      };
      return items.filter((item) => {
        const vessel = asArray(filters.vessel);
        if (vessel.length) {
          const x = (item.supplyCase?.vessel?.vesselName || '').toLowerCase();
          if (!vessel.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        const caseRef = asArray(filters.caseRef);
        if (caseRef.length) {
          const x = (item.supplyCase?.ref || '').toLowerCase();
          if (!caseRef.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        if (!dateMatches(item.supplyCase?.caseDate, asArray(filters.caseDate), dateRanges?.caseDate)) return false;
        if (!dateMatches(item.supplyCase?.eta, asArray(filters.caseEta), dateRanges?.caseEta)) return false;
        const casePort = asArray(filters.casePort);
        if (casePort.length) {
          const x = (formatCasePort(item.supplyCase?.port) || '').toLowerCase();
          if (!casePort.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        const caseStatus = asArray(filters.caseStatus);
        if (caseStatus.length) {
          const x = (item.supplyCase?.status?.name || '').toLowerCase();
          if (!caseStatus.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        const ref = asArray(filters.ref);
        if (ref.length) {
          const v = item.ref || '';
          if (!ref.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (!dateMatches(item.etaToPort, asArray(filters.etaToPort), dateRanges?.etaToPort)) return false;
        const orderRef = asArray(filters.orderRef);
        if (orderRef.length) {
          const x = (item.purchaseOrder?.ref || '').toLowerCase();
          if (!orderRef.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        if (!dateMatches(item.purchaseOrder?.orderDate, asArray(filters.orderDate), dateRanges?.orderDate)) return false;
        const status = asArray(filters.status);
        if (status.length) {
          const x = (item.status?.name || '').toLowerCase();
          if (!status.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        const priority = asArray(filters.priority);
        if (priority.length) {
          const x = (item.priority?.name || '').toLowerCase();
          if (!priority.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        const wh = asArray(filters.warehouseLocation);
        if (wh.length) {
          const x = (item.warehouseLocation?.name || '').toLowerCase();
          if (!wh.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        const forwarder = asArray(filters.forwarder);
        if (forwarder.length) {
          const x = (item.forwarder?.name || '').toLowerCase();
          if (!forwarder.some((f) => x.includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    },
    applySearch: (items: SupplyForwarding[], searchTerm: string) => {
      const q = searchTerm.toLowerCase();
      return items.filter((item) => {
        const sc = item.supplyCase;
        const po = item.purchaseOrder;
        const parts = [
          item.ref,
          item.notes,
          sc?.ref,
          sc?.description,
          sc?.vessel?.vesselName,
          sc?.vessel?.vesselAbrv,
          sc?.port?.name,
          sc?.port?.code,
          sc?.status?.name,
          po?.ref,
          po?.currency?.code,
          po?.currency?.name,
          item.forwarder?.name,
          item.forwarder?.code,
          item.port?.name,
          item.port?.code,
          item.status?.name,
          item.status?.code,
          item.priority?.name,
          item.priority?.code,
          item.warehouseLocation?.name,
          item.warehouseLocation?.code,
          item.warehouseInvoiceNo,
          item.subAgentInvoiceNo,
          item.vesselAgentInvoiceNo,
          item.costUsd != null ? String(item.costUsd) : '',
          String(item.id),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return parts.includes(q);
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New forwarding',
        showTitle: false,
        variant: 'primary' as const,
        href: '/supplies/supply-forwardings/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    columnDividers: false,
    keyExtractor: (item: SupplyForwarding) => item.id,
  },
  emptyStates: {
    noData: 'Create a forwarding record to track shipments between purchase order and delivery.',
    noSearchResults: 'No forwardings match your search.',
    noFilterResults: 'No forwardings match the filters.',
  },
  delete: {
    title: 'Delete forwarding',
    message: 'Delete this forwarding? You cannot delete it while deliveries are linked.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete forwarding.',
    handler: async (id: number) => {
      await deleteSupplyForwarding(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  form: {
    new: { title: 'New forwarding', subtitle: 'Link a case (and optionally a PO) to logistics tracking', submitText: 'Create' },
    edit: { title: 'Edit forwarding', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.'},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

/** Path must stay in sync with `moduleConfig.route.caseView` in `supply-cases/config.tsx`. */
function supplyForwardingCaseIdForView(row: SupplyForwarding | undefined): number | null {
  const id = row?.supplyCase?.id ?? row?.caseId ?? 0;
  return id > 0 ? id : null;
}

const ForwardingCaseRefColumnCell: React.FC<{ row: SupplyForwarding }> = ({ row }) => {
  const { colors } = useTheme();
  const caseId = supplyForwardingCaseIdForView(row);
  if (caseId == null) {
    return (
      <div className="max-w-[16rem] py-0.5">
        <span className={cn('text-sm', colors.textSecondary)}>—</span>
      </div>
    );
  }
  const displayRef = row.supplyCase?.ref?.trim() || `#${caseId}`;
  const href = `/supplies/supply-cases/${caseId}/case-view`;
  const iconBtnClass =
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900 dark:text-indigo-400 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300';

  return (
    <div className="flex max-w-[16rem] items-center gap-0.5 py-0.5">
      <Link
        to={href}
        className={iconBtnClass}
        title="Open case view"
        aria-label="Open case view"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </Link>
      <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)} title={displayRef}>
        {displayRef}
      </span>
    </div>
  );
};

const fmt2 = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const toFinite = (value: string | number | null | undefined): number => {
  const n = typeof value === 'number' ? value : value != null && value !== '' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : 0;
};

/** Same rule as purchase order detail: Σ(lineTotal × rate) + Σ((expense value + VAT) × rate). */
const purchaseOrderConvertedTotal = (po: SupplyForwarding['purchaseOrder']): number | null => {
  if (!po) return null;
  const rate = toFinite(po.exchangeRate);
  const lines = po.lineItems ?? [];
  const expenses = po.expenses ?? [];
  if (lines.length === 0 && expenses.length === 0) return null;
  let sum = 0;
  for (const line of lines) {
    sum += toFinite(line.lineTotal) * rate;
  }
  for (const e of expenses) {
    sum += (toFinite(e.value) + toFinite(e.vat)) * rate;
  }
  return sum;
};

const formatCasePort = (p: { name: string; code?: string | null } | null | undefined) => {
  if (!p?.name?.trim()) return '';
  return p.name;
};

const NumCell: React.FC<{ value: number | null }> = ({ value }) => {
  const { colors } = useTheme();
  if (value == null || !Number.isFinite(value)) {
    return <span className={cn('block text-right text-sm tabular-nums', colors.textSecondary)}>-</span>;
  }
  return <span className={cn('block text-right text-sm font-medium tabular-nums', colors.text)}>{fmt2(value)}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SupplyForwarding>[] => {
  const caseGroup: ColumnGroupMeta = { id: 'sf-case', header: 'Case' };
  const orderGroup: ColumnGroupMeta = { id: 'sf-order', header: 'Order' };
  const forwardingGroup: ColumnGroupMeta = { id: 'sf-forwarding', header: 'Forwarding' };
  const metaGroup: ColumnGroupMeta = { id: 'sf-meta', header: 'Meta' };
  return [
  {
    key: 'supplyCase.vessel',
    header: 'Vessel',
    sortable: false,
    columnGroup: caseGroup,
    render: (_v, row) => {
      const v = row?.supplyCase?.vessel;
      const label = v?.vesselName?.trim() || '';
      return <TextCell value={label} />;
    }},
  {
    key: 'supplyCase.ref',
    header: 'Case ref',
    sortable: true,
    maxWidth: '16rem',
    columnGroup: caseGroup,
    render: (_v, row) => (row ? <ForwardingCaseRefColumnCell row={row} /> : <TextCell value="" />)},
  {
    key: 'supplyCase.caseDate',
    header: 'Case date',
    sortable: true,
    columnGroup: caseGroup,
    render: (_v, row) => <TextCell value={row?.supplyCase?.caseDate ? formatDate(row.supplyCase.caseDate) : ''} />},
  {
    key: 'supplyCase.eta',
    header: 'Case ETA',
    sortable: true,
    columnGroup: caseGroup,
    render: (_v, row) => <TextCell value={row?.supplyCase?.eta ? formatDate(row.supplyCase.eta) : ''} />},
  {
    key: 'supplyCase.port',
    header: 'Case port',
    sortable: false,
    columnGroup: caseGroup,
    render: (_v, row) => <TextCell value={formatCasePort(row?.supplyCase?.port ?? undefined)} />},
  {
    key: 'supplyCase.status',
    header: 'Case status',
    sortable: false,
    columnGroup: caseGroup,
    render: (_v, row) => <TextCell value={row?.supplyCase?.status?.name} />},
  {
    key: 'ref',
    header: 'Reference',
    sortable: true,
    columnGroup: forwardingGroup,
    render: (v, row) => <TextCell value={v ?? row?.ref} />},
  {
    key: 'etaToPort',
    header: 'ETA to port',
    sortable: true,
    columnGroup: forwardingGroup,
    render: (v) => formatDate(v)},
  {
    key: 'purchaseOrder.ref',
    header: 'Order ref',
    sortable: true,
    columnGroup: orderGroup,
    render: (_v, row) => (
      <TextCell value={row?.purchaseOrder?.ref ?? (row?.purchaseOrderId ? `#${row.purchaseOrderId}` : '')} />
    )},
  {
    key: 'purchaseOrder.orderDate',
    header: 'Order date',
    sortable: true,
    columnGroup: orderGroup,
    render: (_v, row) => (
      <TextCell value={row?.purchaseOrder?.orderDate ? formatDate(row.purchaseOrder.orderDate) : ''} />
    )},
  {
    key: 'purchaseOrder.convertedTotal',
    header: 'Order sum (USD eq.)',
    sortable: false,
    columnGroup: orderGroup,
    render: (_v, row) => <NumCell value={purchaseOrderConvertedTotal(row?.purchaseOrder)} />},
  {
    key: 'status.name',
    header: 'Status',
    sortable: true,
    columnGroup: forwardingGroup,
    render: (_v, row) => (
      <TextCell value={row?.status ? (row.status.name) : ''} />
    )},
  {
    key: 'priority.name',
    header: 'Priority',
    sortable: false,
    columnGroup: forwardingGroup,
    render: (_v, row) => (
      <TextCell
        value={
          row?.priority ? (row.priority.name) : ''
        }
      />
    )},
  {
    key: 'costUsd',
    header: 'Cost (USD)',
    sortable: false,
    columnGroup: forwardingGroup,
    render: (_v, row) => (
      <NumCell
        value={
          row?.costUsd != null && row.costUsd !== ''
            ? typeof row.costUsd === 'number'
              ? row.costUsd
              : Number(row.costUsd)
            : null
        }
      />
    )},
  {
    key: 'warehouseLocation.name',
    header: 'WH location',
    sortable: false,
    columnGroup: forwardingGroup,
    render: (_v, row) => {
      const w = row?.warehouseLocation;
      const label = w ? (w.name) : '';
      return <TextCell value={label} />;
    }},
  {
    key: 'forwarder.name',
    header: 'Forwarder',
    sortable: false,
    columnGroup: forwardingGroup,
    render: (_v, row) => (
      <TextCell
        value={
          row?.forwarder?.name
            ? row.forwarder.name
            : ''
        }
      />
    )},
  {
    key: 'deliveries',
    header: 'Deliveries',
    sortable: false,
    columnGroup: forwardingGroup,
    render: (_v, row) => <TextCell value={String(row?._count?.deliveries ?? 0)} />},
  { key: 'createdAt', header: 'Created', sortable: true, columnGroup: metaGroup, render: (v) => formatDate(v) },
  ];
};

const TableActionsCell: React.FC<{
  item: SupplyForwarding;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const { showError } = useNotification();
  const hasCaseAssigned = (item.caseId ?? 0) > 0;
  const hasDeliveriesOnLinkedOrder = (item.purchaseOrder?._count?.deliveries ?? 0) > 0;
  const deleteEnabled = canDelete && !hasCaseAssigned && !hasDeliveriesOnLinkedOrder;

  const onDeleteBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasDeliveriesOnLinkedOrder) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('FWD_DELETE_LINKED_ORDER_DELIVERIES')}`);
      return;
    }
    if (hasCaseAssigned) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('FWD_DELETE_CASE_ASSIGNED')}`);
    }
  };

  return (
    <>
      <div className="flex items-center flex-nowrap">
        <Link
          to={moduleConfig.module.route.detail(item.id)}
          className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="View"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </Link>
        {canEdit && (
          <Link
            to={moduleConfig.module.route.edit(item.id)}
            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </Link>
        )}
        {canDelete && deleteEnabled ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.id);
            }}
            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ) : canDelete ? (
          <button
            type="button"
            onClick={onDeleteBlocked}
            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ) : null}
      </div>
    </>
  );
};

export const getTableActions = (
  item: SupplyForwarding,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
